// components
import { Form, Input } from "antd";
import { Head } from "components/molecules/Head/Head";
import { useCRPointsExpirySummaryPage } from "containers/CRPointsExpirySummary/useCRPointsExpirySummaryPage";
import Label from "components/atoms/Label/Label";
import Button from "components/atoms/Button/Button";
import Spinner from "components/atoms/Spinner/Spinner";
import PageTitle from "components/molecules/PageTitle/PageTitle";

import styles from "containers/CRPointsExpirySummary/CRPointsExpirySummaryPage.module.css";

// constants
import { CR_POINTS_EXPIRY_SUMMARY as Constants, PageTitles } from "constants/global";

const CRPointsExpirySummaryPage = () => {
	const { proceedToGetCRPointsExpirySummary, loading, form, externalId, pointsExpirySummary, handleExternalId } = useCRPointsExpirySummaryPage();

	const isAfterNow = (date: string) => {
		const now = new Date();
		const pointsValidityTill = new Date(date);
		return pointsValidityTill > now;
	};

	const renderAccountResults = (account: any) => {
		account.expiration_details.sort((a: any, b: any) => {
			return new Date(a.points_validity_till).getTime() - new Date(b.points_validity_till).getTime();
		});
		const hasNotExpiredPointsInTheAccount = account.expiration_details.some((detail: any) => {
			return isAfterNow(detail.points_validity_till);
		});

		if (!hasNotExpiredPointsInTheAccount) {
			return null;
		} else {
			return (
				<div key={account.point_account_id}>
					<div className={styles.accountName}>
						Account name: <strong>{account.point_account_name}</strong>
					</div>

					<table className={styles.pointsExpiryResultsTable}>
						<thead>
							<tr>
								<th>Points Balance</th>
								<th>Expiry Date</th>
							</tr>
						</thead>
						<tbody>
							{account.expiration_details.map((detail: any) => {
								const now = new Date();
								const pointsValidityTill = new Date(detail.points_validity_till);
								if (pointsValidityTill > now) {
									return (
										<tr key={detail.exp_date}>
											<td>{detail.exp_points.toLocaleString()}</td>
											<td>{detail.points_validity_till}</td>
										</tr>
									);
								}
								return null;
							})}
						</tbody>
					</table>
				</div>
			);
		}
	};

	const renderResults = () => {
		const results = pointsExpirySummary?.data?.points_expiry_summary;

		if (results) {
			const hasNotExpiredPoints = results.point_accounts.some((account: any) => {
				return account.expiration_details.some((detail: any) => {
					return isAfterNow(detail.points_validity_till);
				});
			});

			if (!hasNotExpiredPoints) {
				return (
					<div className={styles.expirySummaryResults}>
						<div className={styles.header}>{Constants.RESULTS_HEADER}</div>
						<div className={styles.resultsWarning}>{Constants.NO_EXPIRING_POINTS}</div>
					</div>
				);
			} else {
				return (
					<div className={styles.expirySummaryResults}>
						<div className={styles.header}>{Constants.RESULTS_HEADER}</div>
						<div className={styles.resultsWarning}>{Constants.WARNING_MESSAGE}</div>

						{results.point_accounts.map((account: any) => renderAccountResults(account))}
					</div>
				);
			}
		} else {
			return null;
		}
	};

	return (
		<div className="min-h-screen relative">
			{loading && <Spinner type="transparent" />}
			<PageTitle title={Constants.PAGE_HEADER} subTitle={Constants.PAGE_SUBHEADER} />
			<div className={styles?.root}>
				<Head>{PageTitles.CR_POINTS_EXPIRY_SUMMARY}</Head>
				<Form className={styles?.form} form={form} requiredMark={false} layout="horizontal">
					<Form.Item label={<Label requiredMark={true} label={Constants.EXTERNAL_ID_LABEL} classes={{ label: styles?.basic_input_label }} />} name={Constants.EXTERNAL_ID_LABEL}>
						<Input className={styles?.input_field} placeholder={Constants.EXTERNAL_ID_LABEL_PLACEHOLDER} value={externalId} onChange={(e) => handleExternalId(e)} />
					</Form.Item>
				</Form>
				<div className={styles.buttonWrapper}>
					<Button label={Constants.GET_BUTTON_LABEL} type={externalId ? "activeLong" : "disabledLong"} onClick={proceedToGetCRPointsExpirySummary} />
				</div>
				{renderResults()}
			</div>
		</div>
	);
};

export default CRPointsExpirySummaryPage;
